import api from '@/lib/axiosInstance';

/** Helpers for the browser side of web push notifications. */

export const isWebPushSupported = () => {
    return (
        typeof window !== 'undefined' &&
        'serviceWorker' in navigator &&
        'PushManager' in window &&
        'Notification' in window
    );
};

export const getNotificationPermission = (): NotificationPermission | 'unsupported' => {
    if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
    return Notification.permission;
};

/** Convert a VAPID base64-url public key into the Uint8Array PushManager expects. */
const urlBase64ToUint8Array = (base64String: string): Uint8Array => {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = atob(base64);
    const arr = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; ++i) arr[i] = raw.charCodeAt(i);
    return arr;
};

const arrayBufferToBase64Url = (buffer: ArrayBuffer | null): string => {
    if (!buffer) return '';
    const bytes = new Uint8Array(buffer);
    let binary = '';
    bytes.forEach(byte => { binary += String.fromCharCode(byte); });
    return window.btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

export const getPushSubscriptionKeys = (subscription: PushSubscription): { p256dh: string; auth: string } => {
    const jsonKeys = subscription.toJSON().keys || {};
    const p256dh = jsonKeys.p256dh || arrayBufferToBase64Url(subscription.getKey('p256dh'));
    const auth = jsonKeys.auth || arrayBufferToBase64Url(subscription.getKey('auth'));
    return { p256dh, auth };
};

let inflightRegister: Promise<{ status: boolean; reason?: string }> | null = null;

const registerInner = async (): Promise<{ status: boolean; reason?: string }> => {
    if (!isWebPushSupported()) {
        return { status: false, reason: 'unsupported' };
    }
    if (Notification.permission !== 'granted') {
        return { status: false, reason: Notification.permission };
    }

    const registration = await navigator.serviceWorker.ready;
    if (!registration.pushManager) {
        throw new Error('Push subscription is not available in this browser context.');
    }

    const keyRes = await api.get('/me/web-push/public-key');
    if (!keyRes.data.status || !keyRes.data.publicKey) {
        throw new Error(keyRes.data.message || keyRes.data.error || 'Web push public key is unavailable.');
    }

    const publicKey = keyRes.data.publicKey;
    const normalizedPublicKey = publicKey.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    const applicationServerKey = urlBase64ToUint8Array(publicKey);
    let subscription = await registration.pushManager.getSubscription();
    if (subscription) {
        const currentKey = arrayBufferToBase64Url(subscription.options?.applicationServerKey ?? null);
        if (!currentKey || currentKey !== normalizedPublicKey) {
            await subscription.unsubscribe();
            subscription = null;
        }
    }
    if (!subscription) {
        subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey,
        });
    }

    const json = subscription.toJSON();
    const keys = getPushSubscriptionKeys(subscription);
    json.keys = { ...json.keys, ...keys };
    console.log('[webpush] subscription endpoint hash …', (json.endpoint || '').slice(-12));

    const res = await api.post('/me/web-push/subscriptions', {
        subscription: json,
        userAgent: navigator.userAgent,
    }, { headers: { 'Content-Type': 'application/json' } });
    if (!res.data.status) {
        throw new Error(res.data.message || res.data.error);
    }
    return { status: true };
};

export const registerWebPushSubscription = async () => {
    if (inflightRegister) return inflightRegister;
    inflightRegister = registerInner().finally(() => { inflightRegister = null; });
    return inflightRegister;
};

export const registerAgentWebPushSubscription = registerWebPushSubscription;

export const refreshAgentWebPushSubscription = async (): Promise<{ status: boolean; reason?: string }> => {
    if (!isWebPushSupported()) return { status: false, reason: 'unsupported' };
    const registration = await navigator.serviceWorker.ready;
    const existing = await registration.pushManager.getSubscription();
    if (existing) {
        await existing.unsubscribe();
    }
    return registerWebPushSubscription();
};

export const refreshWebPushSubscription = refreshAgentWebPushSubscription;

export const registerPushServiceWorker = async (): Promise<ServiceWorkerRegistration> => {
    if (!isWebPushSupported()) throw new Error('Web push is not supported in this browser.');
    return navigator.serviceWorker.ready;
};

export const subscribeBrowserToPush = async (vapidPublicKey: string): Promise<PushSubscription | null> => {
    const registration = await navigator.serviceWorker.ready;
    let subscription = await registration.pushManager.getSubscription();
    if (subscription) return subscription;
    if (!vapidPublicKey) {
        throw new Error('Server is missing the web push public key. Ask an admin to configure WEB_PUSH_PUBLIC_KEY.');
    }
    subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
    });
    return subscription;
};

export const refreshBrowserPushSubscription = async (vapidPublicKey: string): Promise<{ subscription: PushSubscription | null; oldEndpoint: string | null }> => {
    const oldSub = await getActiveSubscription();
    const oldEndpoint = oldSub?.endpoint || null;
    if (oldSub) await oldSub.unsubscribe();
    const subscription = await subscribeBrowserToPush(vapidPublicKey);
    return { subscription, oldEndpoint };
};

export const getActiveSubscription = async (): Promise<PushSubscription | null> => {
    if (!isWebPushSupported()) return null;
    const reg = await navigator.serviceWorker.getRegistration('/');
    if (!reg) return null;
    return reg.pushManager.getSubscription();
};

export const unsubscribeBrowserFromPush = async (): Promise<string | null> => {
    const sub = await getActiveSubscription();
    if (!sub) return null;
    const endpoint = sub.endpoint;
    await sub.unsubscribe();
    return endpoint;
};
